import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/features/admin/api";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SubmitButton } from "@/components/form/SubmitButton";

export default function AdminNotifications() {
    const qc = useQueryClient();
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [audience, setAudience] = useState<"all" | "active" | "blocked">("active");
    const [error, setError] = useState<string | null>(null);

    const { data: history = [], isLoading } = useQuery({
        queryKey: ["admin", "notifications"],
        queryFn: adminApi.listBroadcasts,
    });

    const sendMut = useMutation({
        mutationFn: () => adminApi.broadcast({ subject, body, audience }),
        onSuccess: (r) => {
            qc.invalidateQueries({ queryKey: ["admin", "notifications"] });
            toast.success(`Sent to ${r.sent} customer${r.sent === 1 ? "" : "s"}`);
            setSubject("");
            setBody("");
        },
        onError: (err) => setError(formatApiError(err, "Broadcast failed")),
    });

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        sendMut.mutate();
    };

    return (
        <div data-testid="admin-notifications-page" className="grid gap-6 lg:grid-cols-3">
            <form
                onSubmit={onSubmit}
                data-testid="admin-notification-form"
                className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft lg:col-span-2 space-y-4"
            >
                <div>
                    <h1 className="text-2xl font-extrabold sm:text-3xl">Send notification</h1>
                    <p className="text-sm text-muted-foreground">
                        Broadcast promotional or transactional messages. Delivery is currently
                        logged (email provider is pluggable — swap in SendGrid / Resend when ready).
                    </p>
                </div>

                <div>
                    <Label>Audience</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {(
                            [
                                { v: "all", l: "All customers" },
                                { v: "active", l: "Active only" },
                                { v: "blocked", l: "Blocked users" },
                            ] as const
                        ).map((a) => (
                            <button
                                key={a.v}
                                type="button"
                                onClick={() => setAudience(a.v)}
                                data-testid={`admin-notification-audience-${a.v}`}
                                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                                    audience === a.v
                                        ? "bg-ink text-white"
                                        : "border border-border hover:bg-muted"
                                }`}
                            >
                                {a.l}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <Label>Subject *</Label>
                    <Input
                        data-testid="admin-notification-subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        required
                        maxLength={140}
                        placeholder="e.g. Flat 20% off today only 🎉"
                    />
                </div>
                <div>
                    <Label>Message *</Label>
                    <textarea
                        data-testid="admin-notification-body"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        required
                        rows={5}
                        maxLength={4000}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        placeholder="Write a warm, on-brand message. Keep it short — customers will read it in a hurry."
                    />
                </div>
                {error ? (
                    <p role="alert" data-testid="admin-notification-error" className="text-sm font-medium text-error">
                        {error}
                    </p>
                ) : null}
                <SubmitButton
                    type="submit"
                    fullWidth={false}
                    loading={sendMut.isPending}
                    data-testid="admin-notification-send"
                >
                    <Send className="mr-1 h-4 w-4" /> Send broadcast
                </SubmitButton>
            </form>

            <aside className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
                <h3 className="text-lg font-bold">Recent broadcasts</h3>
                <ul className="mt-3 space-y-3 text-sm">
                    {isLoading ? (
                        <li className="text-xs text-muted-foreground">Loading…</li>
                    ) : history.length === 0 ? (
                        <li className="text-xs text-muted-foreground">Nothing sent yet.</li>
                    ) : (
                        history.map((h) => (
                            <li key={h.id} className="rounded-xl border border-border/40 p-3">
                                <p className="font-semibold truncate">{h.subject}</p>
                                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                                    {h.body}
                                </p>
                                <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                                    <span className="rounded-full bg-muted px-2 py-0.5">{h.audience}</span>
                                    <span>· {h.recipient_count} recipients</span>
                                </div>
                                <p className="mt-1 text-[10px] text-muted-foreground">
                                    {new Date(h.sent_at).toLocaleString("en-IN")}
                                </p>
                            </li>
                        ))
                    )}
                </ul>
            </aside>
        </div>
    );
}
